import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { ShieldCheck, DownloadSimple, Warning } from "@phosphor-icons/react";
import { dt } from "../lux/format";
import { toast } from "sonner";

// DATA-001: admin data export. The server does all masking and removal; this
// card only picks what to export and saves the zip it returns.
const when = (s) => (s ? dt(s, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const count = (n) => `${Number(n || 0).toLocaleString()} ${n === 1 ? "row" : "rows"}`;

async function blobError(e) {
  const data = e?.response?.data;
  if (data instanceof Blob) {
    try { return JSON.parse(await data.text()).detail || "The export did not go through."; } catch { return "The export did not go through."; }
  }
  return formatApiError(e);
}

export default function DataExportSettings() {
  const [areas, setAreas] = useState(null);
  const [log, setLog] = useState([]);
  const [picked, setPicked] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [withDetails, setWithDetails] = useState(false);
  const [busy, setBusy] = useState("");

  const loadLog = () => api.get("/data-export/log").then((r) => setLog(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/data-export/areas").then((r) => setAreas(r.data)).catch((e) => toast.error(formatApiError(e)));
    loadLog();
  }, []);

  if (!areas) return null;
  const all = areas.map((a) => a.key);
  const labelOf = Object.fromEntries(areas.map((a) => [a.key, a.label]));
  const toggle = (k, on) => setPicked((p) => (on ? [...p, k] : p.filter((x) => x !== k)));

  const run = async (keys, which) => {
    setBusy(which);
    try {
      const res = await api.post("/data-export", {
        areas: keys, include_customer_details: withDetails, date_from: from || null, date_to: to || null,
      }, { responseType: "blob" });
      const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "01motors-export.zip";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Export downloaded");
      loadLog();
    } catch (e) { toast.error(await blobError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="border border-border bg-card" data-testid="data-export-card">
      <div className="px-8 py-6 border-b border-border">
        <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-primary" /><div className="label-mono">Admin</div></div>
        <h2 className="font-heading text-2xl mt-1">Data export</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Download CRM data as a zip of spreadsheet files. Customer names, phones, emails, addresses and notes are replaced with
          stand-ins unless you include customer details. Passwords, keys, card details, signatures and files are never exported.
        </p>
      </div>

      <div className="px-8 py-6 space-y-8">
        <div>
          <div className="flex items-center justify-between gap-4 mb-3">
            <Label className="label-mono">Areas</Label>
            <button type="button" className="text-xs text-primary hover:underline" data-testid="data-export-select-all"
              onClick={() => setPicked(picked.length === all.length ? [] : all)}>
              {picked.length === all.length ? "Clear all" : "Select all"}
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {areas.map((a) => (
              <label key={a.key} className="flex items-start gap-3 cursor-pointer" data-testid={`data-export-area-${a.key}`}>
                <Checkbox checked={picked.includes(a.key)} onCheckedChange={(v) => toggle(a.key, !!v)} className="mt-0.5" aria-label={a.label} />
                <span className="min-w-0">
                  <span className="text-sm">{a.label}</span>
                  <span className="text-xs text-muted-foreground ml-2 tnum">{count(a.rows)}</span>
                  <span className="block text-xs text-muted-foreground truncate" title={a.tables.map((t) => t.table).join(", ")}>
                    {a.tables.length ? a.tables.map((t) => t.table.replace(/_/g, " ")).join(", ") : "nothing yet"}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
          <div>
            <Label className="label-mono mb-1.5 block" htmlFor="data-export-from">Created from</Label>
            <Input id="data-export-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-input border-border w-44" data-testid="data-export-from" />
          </div>
          <div>
            <Label className="label-mono mb-1.5 block" htmlFor="data-export-to">To</Label>
            <Input id="data-export-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bg-input border-border w-44" data-testid="data-export-to" />
          </div>
          <p className="text-xs text-muted-foreground pb-2.5">Leave both empty for everything. Team and Settings are always complete.</p>
        </div>

        <div>
          <label className="flex items-start gap-3 cursor-pointer" data-testid="data-export-details">
            <Checkbox checked={withDetails} onCheckedChange={(v) => setWithDetails(!!v)} className="mt-0.5" aria-label="Include customer details" />
            <span>
              <span className="text-sm">Include customer details</span>
              <span className="block text-xs text-muted-foreground">Real names, phones, emails, addresses and notes. Only tick this when you need them.</span>
            </span>
          </label>
          {withDetails ? (
            <p className="text-xs text-amber-400 mt-2 flex items-start gap-1.5" data-testid="data-export-details-warning">
              <Warning size={14} className="mt-px shrink-0" />
              The file will hold real customer contact details. Keep it somewhere safe and delete it when you are done.
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-3">
          <Button onClick={() => run(picked, "picked")} disabled={!!busy || !picked.length}
            className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="data-export-download">
            <DownloadSimple size={16} className="mr-2" />
            {busy === "picked" ? "Preparing…" : picked.length ? `Download ${picked.length === all.length ? "all areas" : picked.length === 1 ? labelOf[picked[0]] : `${picked.length} areas`}` : "Pick an area"}
          </Button>
          <Button variant="outline" onClick={() => run(all, "full")} disabled={!!busy} data-testid="data-export-full">
            {busy === "full" ? "Preparing…" : "Full export"}
          </Button>
        </div>

        {log.length ? (
          <div>
            <Label className="label-mono mb-2 block">Recent exports</Label>
            <ul className="space-y-1.5" data-testid="data-export-log">
              {log.slice(0, 5).map((r) => (
                <li key={r.id} className="text-xs text-muted-foreground flex flex-wrap gap-x-2">
                  <span className="text-foreground tnum">{when(r.at)}</span>
                  <span>{r.by_name}</span>
                  <span>{r.areas.length === all.length ? "Full export" : r.areas.map((k) => labelOf[k] || k).join(", ")}</span>
                  <span className={r.include_customer_details ? "text-amber-400" : ""}>
                    {r.include_customer_details ? "with customer details" : "customer details masked"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
